import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getBillingPeriod } from '../utils/billingPeriod';
import { getBillingViews } from '../api';
import { siteOf, naturalCompare } from '../utils/siteGroups';

const KEYS = {
  series: 'power.series',
  theme: 'power.theme',
  billingView: 'power.billingView',
  billingViewsCache: 'power.billingViewsCache',
  alerts: 'power.alerts',
  dangerSeen: 'power.dangerSeen',
};

export const DEFAULT_ALERTS = {
  enabled: true,
  // What counts as alert-worthy: 'danger' = only red blocks (≥95% of period
  // high — real risk of a new high), 'warning' = amber + red (≥90%),
  // 'custom' = thresholdPct below.
  triggerLevel: 'danger',
  thresholdPct: 96,             // used only when triggerLevel = 'custom'
  leadTimeHours: 12,            // warn this many hours before the hour lands
  reminderMinutes: 60,          // how often to re-nag after first alert
  tabFlash: true,
  browser: {
    enabled: true,
    requireInteraction: true,   // notification stays on screen until dismissed
    repeatWithReminders: false, // re-notify every reminder interval, not just on discovery
    includeWeather: true,       // also pop for weather-inferred risks
  },
  sound: { enabled: true, name: 'chime', volume: 0.7 },
  discord: { enabled: false, webhookUrl: '', mentionUserId: '' },
  // Weather-inferred alerts are separate because they come from a less
  // reliable signal than MISO's published forecast. User can disable them
  // entirely, and cap how far out we'll take them seriously.
  weather: {
    includeInAlerts: true,
    maxDaysAhead: 10,
    thresholdPct: 100,          // historical max power must be ≥ this % of period high to alert
  },
};

// The site is presented per billing view: locations sharing a billing
// source + schedule are one selection (each Ameren bill group, SWEC,
// Rantoul). Views come from /billing_views; each carries its own current
// pay-period window, so the period high and all table thresholds are
// computed per view. Cached in localStorage so reloads don't wait on the API.

function loadCachedViews() {
  try {
    const raw = localStorage.getItem(KEYS.billingViewsCache);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {}
  return [];
}

// Human labels: the distinct site names behind the view's gauges.
function decorateView(view) {
  const sites = Array.from(new Set((view.locations || []).map(siteOf))).sort(naturalCompare);
  const label = sites.join(' · ') || view.company || view.id;
  const suffix = view.bill_group != null ? `grp ${view.bill_group}` : view.company;
  return { ...view, sites, label, optionLabel: `${label} — ${suffix}` };
}

// Until /billing_views has ever answered, behave like the old default:
// one calendar-month cycle over everything.
const FALLBACK_VIEW = decorateView({
  id: 'calendar', company: null, bill_group: null, schedule: 'calendar-month',
  locations: [], period: null,
});
FALLBACK_VIEW.label = 'All locations';
FALLBACK_VIEW.optionLabel = 'All locations — calendar month';

export function periodOfView(view) {
  const base = { id: view.id, name: view.label, company: view.company, billGroup: view.bill_group };
  if (!view.period) return { ...base, ...getBillingPeriod(1) };
  const start = new Date(view.period.startISO + 'T00:00:00');
  const end = new Date(view.period.endISO + 'T00:00:00');
  return {
    ...base,
    start, end,
    startISO: view.period.startISO,
    endISO: view.period.endISO,
    lengthDays: Math.round((end - start) / 86400000) + 1,
  };
}

const Ctx = createContext(null);

export function PowerProvider({ children }) {
  const [series, setSeries] = useState(() => localStorage.getItem(KEYS.series) || 'LRZ4');
  const [theme, setTheme] = useState(() => localStorage.getItem(KEYS.theme) || 'dark');
  const [views, setViews] = useState(loadCachedViews);
  const [activeViewId, setActiveViewId] = useState(() => localStorage.getItem(KEYS.billingView) || '');
  const [alerts, setAlerts] = useState(() => {
    try {
      const raw = localStorage.getItem(KEYS.alerts);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Migrate pre-Aug-2026 boolean `browserNotification` into the
        // `browser` options object; deep-merge nested groups so new fields
        // get their defaults for existing users.
        const merged = {
          ...DEFAULT_ALERTS,
          ...parsed,
          browser: {
            ...DEFAULT_ALERTS.browser,
            ...(typeof parsed.browserNotification === 'boolean'
              ? { enabled: parsed.browserNotification } : {}),
            ...(parsed.browser || {}),
          },
          sound: { ...DEFAULT_ALERTS.sound, ...(parsed.sound || {}) },
          discord: { ...DEFAULT_ALERTS.discord, ...(parsed.discord || {}) },
          weather: { ...DEFAULT_ALERTS.weather, ...(parsed.weather || {}) },
        };
        delete merged.browserNotification;
        return merged;
      }
    } catch {}
    return DEFAULT_ALERTS;
  });
  const updateAlerts = (patch) => setAlerts(prev => ({ ...prev, ...patch }));

  // Which danger sets the user has already looked at, per billing view.
  // Keyed by view id → signature of the danger-hour set; when a new red-block
  // hour appears for a view, its signature changes and it counts as unseen
  // again, so the topbar badge re-flags it.
  const [dangerSeen, setDangerSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEYS.dangerSeen)) || {}; } catch { return {}; }
  });
  const markDangerSeen = useCallback((entries) => {
    setDangerSeen(prev => {
      let changed = false;
      const next = { ...prev };
      for (const { id, signature } of entries) {
        if (next[id] !== signature) { next[id] = signature; changed = true; }
      }
      return changed ? next : prev;
    });
  }, []);

  const refreshViews = useCallback(async () => {
    try {
      const data = await getBillingViews();
      if (Array.isArray(data?.views) && data.views.length) {
        setViews(data.views);
        localStorage.setItem(KEYS.billingViewsCache, JSON.stringify(data.views));
      }
    } catch {}
  }, []);
  useEffect(() => { refreshViews(); }, [refreshViews]);

  useEffect(() => { localStorage.setItem(KEYS.series, series); }, [series]);
  useEffect(() => { localStorage.setItem(KEYS.theme, theme); document.body.dataset.theme = theme; }, [theme]);
  useEffect(() => { if (activeViewId) localStorage.setItem(KEYS.billingView, activeViewId); }, [activeViewId]);
  useEffect(() => { localStorage.setItem(KEYS.alerts, JSON.stringify(alerts)); }, [alerts]);
  useEffect(() => { localStorage.setItem(KEYS.dangerSeen, JSON.stringify(dangerSeen)); }, [dangerSeen]);

  const decorated = useMemo(() => views.map(decorateView), [views]);
  const activeView = decorated.find(v => v.id === activeViewId) || decorated[0] || FALLBACK_VIEW;
  const activePeriod = useMemo(() => periodOfView(activeView), [activeView]);

  return (
    <Ctx.Provider
      value={{
        series, setSeries,
        theme, setTheme,
        views: decorated, activeView, activeViewId: activeView.id, setActiveViewId,
        refreshViews, activePeriod,
        alerts, updateAlerts,
        dangerSeen, markDangerSeen,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePower() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePower must be used inside <PowerProvider>');
  return ctx;
}
